import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { Fade, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { GraphDonutChart } from '../graphs/graph-donut-chart';
import { energyValueFormatter, valueFormatter } from '../../../../shared/interfaces/analytics';
import { stackedRowTotals } from '../../../../shared/utils/analytics-utils';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils';
import { AnalyticMetric } from '../graphs/analytic-metric';
import { GraphBarChartStacked } from '../graphs/graph-bar-chart-stacked';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { AnalyticsByEnergySkeleton } from './analytics-by-energy-skeleton';
import { ISecrEnergySourceLine } from '../../../../shared/services/generated-report/generated-report.model';
import { type AnalyticsEmissionTypeToggle } from '../../../../shared/utils/default-market-based-utils';

const fmtTco2e = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const EM_DASH = '–';

function fmtCell(value: number): string {
  return value === 0 ? EM_DASH : fmtTco2e.format(value);
}

// The energy time series is single-series: the backend returns one row per month keyed `value`
// (analytics.helper.js, formatBreakdownForStacked). Naming it here stops the chart falling back
// to that raw key as its legend label.
const ENERGY_HISTORY_SERIES = [
  { dataKey: 'value', label: 'Energy consumption', stack: 'total', valueFormatter: energyValueFormatter },
];

function formatSourceList(sources: string[]): string {
  if (sources.length <= 1) return sources.join('');
  return `${sources.slice(0, -1).join(', ')} and ${sources[sources.length - 1]}`;
}

export const AnalyticsByEnergy: FunctionComponent<{
  reportPeriodId: string;
  setLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  /** When set, charts carry the comparison period (tooltip on the donut, grey bars elsewhere). */
  comparisonPeriod?: { id: string; label: string };
  periodLabel?: string;
  /** Scope 2 basis for the emissions widgets. The kWh widgets have no basis and ignore it. */
  emissionType?: AnalyticsEmissionTypeToggle;
}> = ({ reportPeriodId, setLoaded, comparisonPeriod, periodLabel, emissionType = 'locationBased' }) => {
  // Services
  const analyticsService = new AnalyticsService();
  const reportPeriodContext = useContext(ReportPeriodContext);
  // State
  const [pieEnergyEmissions, setPieEnergyEmissions] = useState<any>();
  const [breakdownTotal, setBreakdownTotal] = useState<any>();
  const [stackedEnergyEmissions, setStackedEnergyEmissions] = useState<any>();
  const [energySourceBreakdown, setEnergySourceBreakdown] = useState<ISecrEnergySourceLine[] | null>(null);
  const [comparisonPieEnergy, setComparisonPieEnergy] = useState<any>();
  const [comparisonStackedEnergy, setComparisonStackedEnergy] = useState<any>();
  const [comparisonSourceBreakdown, setComparisonSourceBreakdown] = useState<ISecrEnergySourceLine[] | null>(null);
  const [comparisonBreakdownTotal, setComparisonBreakdownTotal] = useState<any>();
  // Colors
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);
  // Variables
  const requiredReportPeriodId =
    reportPeriodId || reportPeriodContext?.lastSelectedReportId;

  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      if (setLoaded) setLoaded(true);
      return;
    }

    setIsLoading(true);
    const expectedRequests = comparisonPeriod ? 8 : 4;
    let completedRequests = 0;
    const checkLoadingStatus = () => {
      completedRequests += 1;
      if (completedRequests === expectedRequests) {
        setIsLoading(false);
        if (setLoaded) setLoaded(true);
      }
    };

    if (comparisonPeriod) {
      analyticsService
        .getPieData(comparisonPeriod.id, `?data=energy&topN=all`)
        .then((response) => { setComparisonPieEnergy(response); checkLoadingStatus(); })
        .catch((err) => { console.log(err); setComparisonPieEnergy(undefined); checkLoadingStatus(); });

      analyticsService
        .getStackedData(comparisonPeriod.id, `?data=energy`)
        .then((response) => { setComparisonStackedEnergy(response.breakdown); checkLoadingStatus(); })
        .catch((err) => { console.log(err); setComparisonStackedEnergy(undefined); checkLoadingStatus(); });

      analyticsService
        .getEnergySourceBreakdown(comparisonPeriod.id)
        .then((response) => { setComparisonSourceBreakdown(response); checkLoadingStatus(); })
        .catch((err) => { console.log(err); setComparisonSourceBreakdown(null); checkLoadingStatus(); });

      analyticsService
        .getEnergyBreakdownTotal(comparisonPeriod.id)
        .then((response) => { setComparisonBreakdownTotal(response); checkLoadingStatus(); })
        .catch((err) => { console.log(err); setComparisonBreakdownTotal(undefined); checkLoadingStatus(); });
    } else {
      setComparisonPieEnergy(undefined);
      setComparisonStackedEnergy(undefined);
      setComparisonSourceBreakdown(null);
      setComparisonBreakdownTotal(undefined);
    }

    analyticsService
      .getStackedData(requiredReportPeriodId, `?data=energy`)
      .then((response) => {
        setStackedEnergyEmissions(response.breakdown);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    analyticsService
      .getEnergyBreakdownTotal(requiredReportPeriodId)
      .then((response) => {
        setBreakdownTotal(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    analyticsService
      .getPieData(requiredReportPeriodId, `?data=energy&topN=all`)
      .then((response) => {
        setPieEnergyEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    analyticsService
      .getEnergySourceBreakdown(requiredReportPeriodId)
      .then((response) => {
        setEnergySourceBreakdown(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });
  }, [requiredReportPeriodId, comparisonPeriod?.id]);

  // Scope 2 is dual-reported, so these widgets follow the page's basis toggle instead of pinning
  // location-based: an organisation on a market-based default was otherwise shown a figure here
  // that did not match its own overview tab or its SECR report.
  const isMarketBasis = emissionType === 'marketBased';
  const basisLabel = isMarketBasis ? 'market-based' : 'location-based';
  // The legend gives each entry a fixed 180px, and "Scope 2 (location-based)" breaks mid-word in
  // it, so the swatch takes the short form and the tooltip and table caption carry the full one.
  const seriesBasisLabel = isMarketBasis ? 'market' : 'location';
  // A source with no market-based row returns null, not zero. Keep that distinction: a blank is
  // "not recorded", which is not the same claim as "no emissions".
  const scopeTwoOf = (line: ISecrEnergySourceLine): number | null =>
    isMarketBasis ? line.global.scope2MarketTco2e ?? null : line.global.scope2LocationTco2e ?? null;

  const sourceChartDataset = energySourceBreakdown
    // Filtered on both bases so the source rows stay put as the toggle moves.
    ?.filter((l) => (l.global.scope1Tco2e ?? 0) + (l.global.scope2LocationTco2e ?? 0) + (l.global.scope2MarketTco2e ?? 0) > 0)
    .map((l) => ({
      source: l.label,
      scope1: l.global.scope1Tco2e ?? 0,
      scope2: scopeTwoOf(l) ?? 0,
      // This source has a scope 2 figure on the other basis but none on this one.
      scope2Missing: scopeTwoOf(l) == null && (l.global.scope2LocationTco2e ?? l.global.scope2MarketTco2e ?? 0) > 0,
    }));

  const missingBasisSources = sourceChartDataset?.filter((row) => row.scope2Missing).map((row) => row.source) ?? [];

  const sourceChartSeries = [
    { dataKey: 'scope1', label: 'Scope 1', stack: 'total' },
    { dataKey: 'scope2', label: `Scope 2 (${seriesBasisLabel})`, stack: 'total' },
  ];

  const prevEnergyTotals = comparisonPeriod ? comparisonBreakdownTotal : undefined;
  const energyComparisonLabel = comparisonPeriod ? `vs ${comparisonPeriod.label}` : undefined;

  // Comparison totals aligned to the CURRENT dataset's source order by label, not row index:
  // the two periods can have different source sets.
  const comparisonSourceTotals = sourceChartDataset && comparisonPeriod && comparisonSourceBreakdown
    ? sourceChartDataset.map((row) => {
      const match = comparisonSourceBreakdown.find((line) => line.label === row.source);
      return match ? (match.global.scope1Tco2e ?? 0) + (scopeTwoOf(match) ?? 0) : 0;
    })
    : undefined;

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <div>
            <AnalyticsByEnergySkeleton />
          </div>
        </Fade>
      )}
      {!isLoading && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container spacing={2}>
              {breakdownTotal && (
                <>
                  <Grid
                    item
                    xl={4}
                    lg={4}
                    md={6}
                    sm={12}
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <AnalyticMetric
                      title="Total Energy Consumption"
                      tooltip="The total energy consumption in kWh for the selected report period"
                      value={breakdownTotal.total}
                      units="kWh"
                      comparison={prevEnergyTotals ? breakdownTotal.total - prevEnergyTotals.total : undefined}
                      comparisonLabel={energyComparisonLabel}
                    />
                  </Grid>
                  {/* Entity-scoped sessions get perEmployee: null (org-wide denominator is
                      meaningless against an entity numerator): hide the tile. */}
                  {breakdownTotal.perEmployee != null &&
                    <Grid
                      item
                      xl={4}
                      lg={4}
                      md={6}
                      sm={12}
                      xs={12}
                      sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <AnalyticMetric
                        title="Energy Consumption per Employee"
                        value={breakdownTotal.perEmployee}
                        units="kWh"
                        comparison={prevEnergyTotals?.perEmployee != null ? breakdownTotal.perEmployee - prevEnergyTotals.perEmployee : undefined}
                        comparisonLabel={energyComparisonLabel}
                      />
                    </Grid>
                  }
                  <Grid
                    item
                    xl={4}
                    lg={4}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <AnalyticMetric
                      units="kWh"
                      title="UK Energy Consumption"
                      titleTwo="Non UK Energy Consumption"
                      value={breakdownTotal.ukTotal}
                      valueTwo={breakdownTotal.nonUkTotal}
                      toggleOne="UK"
                      toggleTwo="Non UK"
                      comparison={prevEnergyTotals ? breakdownTotal.ukTotal - prevEnergyTotals.ukTotal : undefined}
                      comparisonTwo={prevEnergyTotals ? breakdownTotal.nonUkTotal - prevEnergyTotals.nonUkTotal : undefined}
                      comparisonLabel={energyComparisonLabel}
                    />
                  </Grid>
                </>
              )}
              {pieEnergyEmissions && (
                <Grid
                  item
                  xl={4}
                  lg={4}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphDonutChart
                    enableTopN
                    defaultTopN={5}
                    title="Energy Source Breakdown"
                    tooltip="The breakdown of energy sources used for consumption"
                    dataset={pieEnergyEmissions.breakdown}
                    colors={colors}
                    energyValue={true}
                    periodLabel={periodLabel}
                    comparison={comparisonPeriod && comparisonPieEnergy?.breakdown
                      ? { dataset: comparisonPieEnergy.breakdown, label: comparisonPeriod.label }
                      : undefined}
                  />
                </Grid>
              )}
              {stackedEnergyEmissions && (
                <Grid
                  item
                  xl={8}
                  lg={8}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphBarChartStacked
                    title="Energy Consumption History"
                    tooltip="The energy consumption history for each month in the selected report period per energy"
                    dataset={stackedEnergyEmissions}
                    colors={colors}
                    series={ENERGY_HISTORY_SERIES}
                    // One series needs no legend, but once a comparison period adds its own grey
                    // swatch the pair has to be named for the colours to mean anything.
                    hideSeriesLegend={!comparisonPeriod}
                    comparison={comparisonPeriod && comparisonStackedEnergy
                      ? {
                        totals: stackedRowTotals(comparisonStackedEnergy),
                        label: comparisonPeriod.label,
                        valueFormatter: energyValueFormatter,
                      }
                      : undefined}
                  />
                </Grid>
              )}
              {sourceChartDataset && sourceChartDataset.length > 0 && (
                <>
                  <Grid item xl={8} lg={8} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <GraphBarChartStacked
                      title="Emissions by Energy Source"
                      tooltip={`Scope 1 and Scope 2 emissions (tCO₂e) broken down by energy source for the selected report period. Scope 2 follows the ${basisLabel} basis selected above.${missingBasisSources.length > 0 ? ` No ${basisLabel} figure is recorded for ${formatSourceList(missingBasisSources)}, so nothing is plotted for those sources.` : ''}`}
                      dataset={sourceChartDataset}
                      colors={colors}
                      series={sourceChartSeries}
                      xAxisDataKey="source"
                      comparison={comparisonPeriod && comparisonSourceTotals
                        ? { totals: comparisonSourceTotals, label: comparisonPeriod.label, valueFormatter }
                        : undefined}
                    />
                  </Grid>
                  <Grid item xl={4} lg={4} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Paper variant="outlined" sx={{ p: 4, height: '100%' }}>
                      <Stack direction="row" alignItems="center">
                        <Typography variant="subtitle1" sx={{ flex: 1 }}>
                          <b>Scope breakdown by source</b>
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
                        Scope 2 shown on a {basisLabel} basis.
                        {missingBasisSources.length > 0 && (missingBasisSources.length === 1
                          ? ` No ${basisLabel} figure is recorded for ${missingBasisSources[0]}, so that row is blank rather than zero and the total leaves it out.`
                          : ` No ${basisLabel} figure is recorded for ${formatSourceList(missingBasisSources)}, so those rows are blank rather than zero and the total leaves them out.`)}
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, pl: 0 }}>Source</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Scope 1 (tCO₂e)</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Scope 2 (tCO₂e)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sourceChartDataset.map((row) => (
                              <TableRow key={row.source}>
                                <TableCell sx={{ pl: 0 }}>{row.source}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: row.scope1 === 0 ? 'text.secondary' : 'inherit' }}>
                                  {fmtCell(row.scope1)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: row.scope2 === 0 ? 'text.secondary' : 'inherit' }}>
                                  {row.scope2Missing ? EM_DASH : fmtCell(row.scope2)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ '& .MuiTableCell-root': { borderTop: '2px solid', borderTopColor: 'divider', borderBottom: 'none', fontWeight: 700 } }}>
                              <TableCell sx={{ pl: 0 }}>Total</TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {fmtCell(sourceChartDataset.reduce((s, r) => s + r.scope1, 0))}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {fmtCell(sourceChartDataset.reduce((s, r) => s + (r.scope2Missing ? 0 : r.scope2), 0))}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                </>
              )}
            </Grid>
          </div>
        </Fade>
      )}
    </>
  );
};
